import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';

const LAST_UPDATED = 'April 29, 2026';

const SECTIONS = [
  { id: 'introduction',       label: 'Introduction' },
  { id: 'information',        label: 'Information We Collect' },
  { id: 'use',                label: 'How We Use Your Information' },
  { id: 'sharing',            label: 'Sharing of Information' },
  { id: 'security',           label: 'Data Security' },
  { id: 'rights',             label: 'Your Rights' },
  { id: 'retention',          label: 'Data Retention' },
  { id: 'cookies',            label: 'Cookies & Tracking' },
  { id: 'third-party',        label: 'Third-Party Services' },
  { id: 'children',           label: "Children's Privacy" },
  { id: 'changes',            label: 'Changes to This Policy' },
  { id: 'contact',            label: 'Contact Us' },
];

const PrivacyPolicy = () => {
  const [active, setActive] = useState('introduction');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => { if (e.isIntersecting) setActive(e.target.id); });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    SECTIONS.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <Seo
        title="Privacy Policy — InvFly"
        description="Learn how InvFly collects, uses, and protects your personal information."
      />

      {/* Hero */}
      <div className="ph">
        <div className="container">
          <div className="ph-tag">Legal</div>
          <div className="ph-title">Privacy <span className="txt-ac">Policy</span></div>
          <p className="ph-desc">Last updated: {LAST_UPDATED}</p>
        </div>
      </div>

      <section className="sec">
        <div className="container">
          <div style={{ display: 'flex', gap: '48px', alignItems: 'flex-start' }}>

            {/* Sticky Table of Contents */}
            <aside style={{
              width: '220px',
              flexShrink: 0,
              position: 'sticky',
              top: '88px',
              alignSelf: 'flex-start',
              display: 'none',
            }} className="policy-toc">
              <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '1.2px', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: '12px' }}>Contents</div>
              <nav>
                {SECTIONS.map(s => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', background: 'none',
                      border: 'none', borderLeft: `2px solid ${active === s.id ? 'var(--accent)' : 'var(--border)'}`,
                      padding: '6px 12px', fontSize: '13px', cursor: 'pointer',
                      color: active === s.id ? 'var(--accent)' : 'var(--text-2)',
                      fontFamily: 'var(--fb)', fontWeight: active === s.id ? '600' : '400',
                      transition: 'color 0.15s, border-color 0.15s',
                      marginBottom: '2px',
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </nav>
            </aside>

            {/* Main Content */}
            <div style={{ flex: 1, maxWidth: '760px' }}>
              <div style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                padding: '8px 16px 12px',
                marginBottom: '32px',
                fontSize: '13px',
                color: 'var(--text-2)',
                lineHeight: '1.7',
              }}>
                This Privacy Policy explains how InvFly ("<strong style={{ color: 'var(--text)' }}>we</strong>", "<strong style={{ color: 'var(--text)' }}>us</strong>", or "<strong style={{ color: 'var(--text)' }}>our</strong>") collects, uses, and protects information about you when you use our website and services. By using InvFly, you agree to the practices described here.
              </div>

              <PolicySection id="introduction" title="1. Introduction">
                <p>InvFly is an online financial education platform operated from Hyderabad, Telangana, India. We are committed to protecting your privacy and handling your personal data transparently and responsibly, in compliance with the <strong>Information Technology Act, 2000</strong> and the <strong>Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011</strong> ("SPDI Rules").</p>
                <p>This Policy applies to all visitors, registered users, and course enrolees of the InvFly platform.</p>
              </PolicySection>

              <PolicySection id="information" title="2. Information We Collect">
                <SubHeading>2.1 Information You Provide</SubHeading>
                <ul>
                  <li><strong>Account registration:</strong> Full name, email address, phone number, and password.</li>
                  <li><strong>Course enrolment &amp; payments:</strong> Transaction details, payment reference numbers, and enrolment history. We do not store card or bank details — payments are processed by third-party gateways.</li>
                  <li><strong>Contact form:</strong> Name, email, phone, and any message content you submit.</li>
                  <li><strong>Communications:</strong> Emails or messages you send us.</li>
                </ul>
                <SubHeading>2.2 Information Collected Automatically</SubHeading>
                <ul>
                  <li><strong>Log data:</strong> IP address, browser type, device type, pages visited, and timestamps.</li>
                  <li><strong>Cookies &amp; local storage:</strong> Authentication tokens and session preferences.</li>
                  <li><strong>Verification data:</strong> OTP records including type (email/phone), request timestamps, and IP address at the time of OTP request — used for security purposes.</li>
                </ul>
                <SubHeading>2.3 Information from Third Parties</SubHeading>
                <ul>
                  <li>If you sign in via Google OAuth, we receive your name, email address, and profile picture as permitted by your Google account settings.</li>
                </ul>
              </PolicySection>

              <PolicySection id="use" title="3. How We Use Your Information">
                <p>We use your information only for the following purposes:</p>
                <ul>
                  <li>Creating and managing your InvFly account.</li>
                  <li>Processing course enrolments and verifying payments.</li>
                  <li>Delivering course materials, updates, and learning resources.</li>
                  <li>Sending transactional emails (OTPs, enrolment confirmations, receipts).</li>
                  <li>Responding to your enquiries and providing customer support.</li>
                  <li>Sending periodic educational newsletters and platform updates (you may opt out at any time).</li>
                  <li>Ensuring platform security, preventing fraud, and enforcing our Terms of Service.</li>
                  <li>Complying with applicable laws and legal obligations.</li>
                  <li>Improving our platform through anonymised usage analytics.</li>
                </ul>
                <p>We will not use your information for purposes beyond those listed above without your explicit consent.</p>
              </PolicySection>

              <PolicySection id="sharing" title="4. Sharing of Information">
                <p>We do not sell, rent, or trade your personal information. We may share it only in these limited circumstances:</p>
                <ul>
                  <li><strong>Service providers:</strong> Trusted third-party vendors who help us operate InvFly — including payment processors (Razorpay/similar), email delivery services (SendGrid), SMS services (Brevo), and bot-protection services (Cloudflare). These parties are contractually bound to protect your data and may not use it for independent purposes.</li>
                  <li><strong>Legal compliance:</strong> When required by Indian law, court order, or government authority.</li>
                  <li><strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of assets, user data may be transferred. We will notify you before your data becomes subject to a different privacy policy.</li>
                  <li><strong>With your consent:</strong> For any purpose you specifically authorise.</li>
                </ul>
              </PolicySection>

              <PolicySection id="security" title="5. Data Security">
                <p>We implement reasonable technical and organisational measures to protect your personal data from unauthorised access, loss, or misuse, including:</p>
                <ul>
                  <li>Passwords stored using industry-standard one-way hashing (bcrypt).</li>
                  <li>HTTPS encryption for all data in transit.</li>
                  <li>OTP-based email and phone verification to confirm identity.</li>
                  <li>Single-use verification tokens that are invalidated after use.</li>
                  <li>Access controls limiting who can access personal data internally.</li>
                </ul>
                <p>No method of transmission over the internet is 100% secure. If you suspect unauthorised access to your account, please contact us immediately.</p>
              </PolicySection>

              <PolicySection id="rights" title="6. Your Rights">
                <p>As a user, you have the following rights regarding your personal data:</p>
                <ul>
                  <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information.</li>
                  <li><strong>Deletion:</strong> Request deletion of your account and associated personal data, subject to legal retention obligations.</li>
                  <li><strong>Withdrawal of consent:</strong> Opt out of marketing communications at any time by clicking "Unsubscribe" in any email or contacting us directly.</li>
                  <li><strong>Grievance redressal:</strong> As required under the SPDI Rules, you may raise a grievance with our designated contact (see Section 12) and we will respond within 30 days.</li>
                </ul>
                <p>To exercise any of these rights, please email us at the address listed in Section 12.</p>
              </PolicySection>

              <PolicySection id="retention" title="7. Data Retention">
                <p>We retain your personal data only as long as necessary:</p>
                <ul>
                  <li><strong>Active accounts:</strong> Data is retained for the duration of your account.</li>
                  <li><strong>Deleted accounts:</strong> Core account data is deleted within 30 days of a deletion request. Anonymised usage records and legally required financial records (transaction data) may be retained for up to 7 years as required by Indian tax and accounting laws.</li>
                  <li><strong>OTP / verification records:</strong> Retained for security auditing purposes for up to 12 months after creation.</li>
                </ul>
              </PolicySection>

              <PolicySection id="cookies" title="8. Cookies &amp; Tracking">
                <p>We use the following types of cookies and local storage:</p>
                <ul>
                  <li><strong>Essential cookies:</strong> Authentication tokens stored in <code style={{ fontFamily: 'var(--fm)', background: 'var(--surface-2)', padding: '1px 5px', borderRadius: '3px' }}>localStorage</code> to keep you signed in. These are required for the platform to function.</li>
                  <li><strong>Security cookies:</strong> Cloudflare Turnstile cookies for bot-protection on forms.</li>
                  <li><strong>Analytics:</strong> We may use anonymised analytics (e.g., page views) to improve our platform. These do not identify you personally.</li>
                </ul>
                <p>You can clear cookies and local storage via your browser settings. Note that this will sign you out of InvFly.</p>
              </PolicySection>

              <PolicySection id="third-party" title="9. Third-Party Services">
                <p>Our platform integrates with the following third-party services, each governed by their own privacy policies:</p>
                <ul>
                  <li><strong>Google OAuth</strong> — for social sign-in (<a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>Google Privacy Policy</a>)</li>
                  <li><strong>Cloudflare Turnstile</strong> — for bot protection on forms</li>
                  <li><strong>SendGrid</strong> — for transactional email delivery</li>
                  <li><strong>Brevo (formerly Sendinblue)</strong> — for SMS OTP delivery</li>
                  <li><strong>Payment gateway</strong> — for processing course payments</li>
                </ul>
                <p>Our website may contain links to external sites. We are not responsible for the privacy practices of those sites and encourage you to review their policies separately.</p>
              </PolicySection>

              <PolicySection id="children" title="10. Children's Privacy">
                <p>InvFly is not directed at children under the age of 18. We do not knowingly collect personal information from minors. If you believe a minor has registered on our platform, please contact us and we will take prompt action to delete the account.</p>
              </PolicySection>

              <PolicySection id="changes" title="11. Changes to This Policy">
                <p>We may update this Privacy Policy from time to time. When we make material changes, we will update the "Last updated" date at the top of this page and, where appropriate, notify you by email or by a prominent notice on our website.</p>
                <p>Your continued use of InvFly after such changes constitutes your acceptance of the revised Policy.</p>
              </PolicySection>

              <PolicySection id="contact" title="12. Contact Us">
                <p>If you have questions, concerns, or wish to exercise your data rights, please reach out:</p>
                <div style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderLeft: '3px solid var(--accent)',
                  borderRadius: '8px',
                  padding: '16px 20px',
                  marginTop: '12px',
                }}>
                  <p style={{ margin: '0 0 4px' }}><strong>InvFly</strong></p>
                  <p style={{ margin: '0 0 4px' }}>Hyderabad, Telangana, India</p>
                  <p style={{ margin: '0' }}>
                    <Link to="/contact" style={{ color: 'var(--accent)' }}>Contact Form</Link>
                  </p>
                </div>
                <p style={{ marginTop: '16px' }}>We aim to respond to all privacy-related enquiries within <strong>30 days</strong>.</p>
              </PolicySection>

            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const PolicySection = ({ id, title, children }) => (
  <section id={id} style={{ marginBottom: '44px', scrollMarginTop: '96px' }}>
    <h2 style={{
      fontSize: '19px', fontWeight: '700', color: 'var(--text)',
      fontFamily: 'var(--fd)', marginBottom: '14px',
      paddingBottom: '10px', borderBottom: '1px solid var(--border)',
    }}>
      {title}
    </h2>
    <div style={{ fontSize: '14.5px', color: 'var(--text-2)', lineHeight: '1.85' }}>
      {children}
    </div>
  </section>
);

const SubHeading = ({ children }) => (
  <p style={{ fontWeight: '600', color: 'var(--text)', marginTop: '14px', marginBottom: '6px', fontSize: '14.5px' }}>
    {children}
  </p>
);

export default PrivacyPolicy;
